import { BlobProvider } from "@react-pdf/renderer";
import React, { ReactNode } from "react";

import { DarkAppButton } from "../../../../../buttons";
import DecisionAidPDFDocument from "../../DecisionAidPDFDocument";
import WantToKnowMore from "../WantToKnowMore/WantToKnowMore";
import {
  PDAContentSection,
  PDAParagraph,
} from "../template/PDAContentComponents";

export interface TreatmentLink {
  title: string;
  url: string;
}

interface TakeActionContentProps {
  // PDF Document props
  pdfTitle: string;
  pdfSubtitle: string;
  pdfIntroTitle: string;
  pdfConditionDescription: string;
  sortedTreatmentOptions: any[];
  treatmentData: Record<string, any>;
  healthFactors: any;
  userNotes: any;
  logoUrl: string;
  doctorInstructions: string;
  keyToOptionIdMapping?: Record<string, string>;

  // State handlers
  pdfBlobForEmail: Blob | null;
  setPdfBlobForEmail: (blob: Blob | null) => void;
  setIsEmailModalOpen: (isOpen: boolean) => void;

  // Treatment links
  treatmentLinks: TreatmentLink[];

  // Calendar image
  imageSrc: string;
}

const TakeActionContent: React.FC<TakeActionContentProps> = ({
  pdfTitle,
  pdfSubtitle,
  pdfIntroTitle,
  pdfConditionDescription,
  sortedTreatmentOptions,
  treatmentData,
  healthFactors,
  userNotes,
  logoUrl,
  doctorInstructions,
  keyToOptionIdMapping,
  pdfBlobForEmail,
  setPdfBlobForEmail,
  setIsEmailModalOpen,
  treatmentLinks,
  imageSrc,
}) => {
  return (
    <div className="max-w-4xl mx-auto">
      <PDAContentSection>
        <PDAParagraph>
          Well done! You have explored your options. You're now equipped with
          the knowledge to take the next step with confidence.
        </PDAParagraph>
      </PDAContentSection>

      <PDAContentSection title="Save your decision aid summary">
        <PDAParagraph>
          Save a summary of your decision aid journey to your phone or send it
          to your email.
        </PDAParagraph>
        <PDAContentSection>
          <div className="flex flex-col space-y-3">
            <BlobProvider
              document={
                <DecisionAidPDFDocument
                  title={pdfTitle}
                  subtitle={pdfSubtitle}
                  introTitle={pdfIntroTitle}
                  conditionDescription={pdfConditionDescription}
                  treatmentOptions={sortedTreatmentOptions}
                  treatmentData={treatmentData}
                  healthFactors={healthFactors}
                  userNotes={userNotes}
                  AppLogo={logoUrl}
                  doctorInstructions={doctorInstructions}
                  keyToOptionIdMapping={keyToOptionIdMapping}
                />
              }
            >
              {({ blob, url, loading, error }) => {
                if (blob && blob !== pdfBlobForEmail) setPdfBlobForEmail(blob);
                return (
                  <div className="flex flex-col  space-y-2 w-full">
                    <DarkAppButton
                      title="Save PDF to your phone"
                      className="flex items-center justify-center text-center"
                      onClick={() => {
                        if (url) {
                          window.open(url, "_blank");
                        }
                      }}
                      disabled={loading || !url}
                    >
                      {loading ? "Generating PDF..." : "Save PDF to your phone"}
                    </DarkAppButton>
                    <DarkAppButton
                      title="Send PDF to email"
                      className="flex items-center justify-center text-center"
                      onClick={() => setIsEmailModalOpen(true)}
                      disabled={loading || !blob}
                    ></DarkAppButton>
                  </div>
                );
              }}
            </BlobProvider>
          </div>
        </PDAContentSection>

        <PDAParagraph>
          Bring this summary to your doctor to support a helpful discussion.
        </PDAParagraph>
      </PDAContentSection>

      <WantToKnowMore links={treatmentLinks} />
      <h1 className="text-2xl font-semibold text-site-blue-dark mb-4">
        Make an appointment with your doctor now
      </h1>

      <div className="rounded-lg overflow-hidden shadow-lg">
        <img
          src={imageSrc}
          alt="Calendar appointment"
          className="w-full h-auto"
        />
      </div>
    </div>
  );
};

export default TakeActionContent;
